import { useState, useEffect } from 'react';
import {
  Box, CircularProgress, Alert, Paper, Tabs, Tab, Button,
} from '@mui/material';
import {
  AccountTree as PipelineIcon,
  People as PeopleIcon,
  Info as InfoIcon,
  PersonAddAlt1 as PersonAddIcon,
  UploadFile as UploadFileIcon,
  Insights as StatsIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import AtsKanbanPipeline from './AtsKanbanPipeline';
import AtsMoveStageDialog from './AtsMoveStageDialog';
import AtsPositionHeader from './AtsPositionHeader';
import AtsPhaseBar from './AtsPhaseBar';
import AtsApplicantsTab from './AtsApplicantsTab';
import AtsPositionInfoTab from './AtsPositionInfoTab';
import AtsPositionStatsTab from './AtsPositionStatsTab';
import AtsAddApplicantsDialog from './AtsAddApplicantsDialog';
import AtsImportApplicantsDialog from './AtsImportApplicantsDialog';
import { canEditPosition } from './positionPermissions';
import { isTerminalStatus } from './positionStatus';
import useOrgPublicSlug from './useOrgPublicSlug';
import { getPositionUrl } from '../../jobBoard/hostSlug';
import { skippedAfterMoveMessage } from './backgroundCheckPricing';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AtsPositionDetailLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { positionId } = useParams();
  const { authToken, user, showAlert, organization, organizationName } = useStore.getState();
  // Subdominio de la organización para "Abrir portal" (link público de la vacante)
  const publicSlug = useOrgPublicSlug();

  const isSuperAdmin = user?.rol === 'super-admin';

  const [position, setPosition] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loadingPosition, setLoadingPosition] = useState(true);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [updatingStage, setUpdatingStage] = useState(null);
  const [updatingPhase, setUpdatingPhase] = useState(false);
  const [closingProcess, setClosingProcess] = useState(false);

  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [pendingStageMove, setPendingStageMove] = useState(null);
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [pendingPhaseMove, setPendingPhaseMove] = useState(null);
  const [addApplicantsOpen, setAddApplicantsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [rejectionTemplate, setRejectionTemplate] = useState({ subject: '', body: '' });
  const [cvScores, setCvScores] = useState({});

  const reloadApplications = async () => {
    try {
      setLoadingApplications(true);
      const response = await api.get(
        `/applications`,
        { params: { positionId }, headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response.data.response?.status_response === 'OK') {
        setApplications(response.data.response.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingApplications(false);
    }
  };

  useEffect(() => {
    const loadPosition = async () => {
      try {
        setLoadingPosition(true);
        const response = await api.get(
          `/positions/${positionId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (response.data.response?.status_response === 'OK') {
          setPosition(response.data.response.data);
        } else {
          setError(t('ats.detail.loadError'));
        }
      } catch (err) {
        console.error(err);
        setError(t('ats.detail.loadError'));
      } finally {
        setLoadingPosition(false);
      }
    };

    const loadApplications = async () => {
      try {
        setLoadingApplications(true);
        const response = await api.get(
          `/applications`,
          { params: { positionId }, headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (response.data.response?.status_response === 'OK') {
          setApplications(response.data.response.data || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingApplications(false);
      }
    };

    if (positionId) {
      loadPosition();
      loadApplications();
    }
    // authToken se lee vía useStore.getState(); se deja fuera de deps para no
    // recargar (y perder el estado de la vista) cuando Firebase refresca el token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);

  // Cargar los scores de filtrado CV completados de esta vacante y armar el mapa
  // applicantId → score. Si un postulante aparece en varios filtrados completados,
  // se usa el del filtrado más reciente.
  const loadCvScores = async () => {
    const projectId = position?.projectId;
    if (!projectId) return;
    try {
      const response = await api.get(`/cv-filterings`, {
        params: { projectId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.errorCode !== 0) return;
      const toMillis = (ts) => {
        if (!ts) return 0;
        if (ts.toDate) return ts.toDate().getTime();
        if (ts._seconds) return ts._seconds * 1000;
        const d = new Date(ts);
        return Number.isNaN(d.getTime()) ? 0 : d.getTime();
      };
      const completed = (response.data.response?.data || [])
        .filter((f) => f.positionId === positionId && f.status === 'completed' && Array.isArray(f.results))
        .sort((a, b) => toMillis(b.created) - toMillis(a.created));
      const map = {};
      completed.forEach((filtering) => {
        filtering.results.forEach((result) => {
          if (result.applicantId && map[result.applicantId] === undefined) {
            map[result.applicantId] = result.score ?? 0;
          }
        });
      });
      setCvScores(map);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadCvScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.projectId, positionId]);

  // Cargar la plantilla del correo de descarte del proyecto (para el diálogo de descarte)
  useEffect(() => {
    const loadRejectionTemplate = async () => {
      const projectId = position?.projectId;
      if (!organization || !projectId) return;
      try {
        const res = await api.get(`/projects/ats-rejection-email`, {
          params: { organizationId: organization, projectId },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data;
        if (data) setRejectionTemplate({ subject: data.subject || '', body: data.body || '' });
      } catch (err) {
        console.error(err);
      }
    };
    loadRejectionTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.projectId, organization]);

  // ── HELPERS ───────────────────────────────────────────────────────────────

  const getPhases = () => position?.pipeline?.phases || [];

  const getAllStages = () => {
    const allStages = [];
    getPhases().forEach((phase) => {
      (phase.stages || []).forEach((stage) => {
        allStages.push({
          ...stage,
          phaseId: phase.id,
          phaseName: phase.name,
          phaseOrder: phase.order,
        });
      });
    });
    return allStages;
  };

  const getCurrentPhase = () => {
    const phases = getPhases();
    const currentPhaseId = position?.pipeline?.currentPhaseId;
    return phases.find((p) => p.id === currentPhaseId) || phases[0] || null;
  };

  const formatSalario = (salarioRango) => {
    if (!salarioRango) return null;
    const currency = salarioRango.currency || '';
    // Sueldo único (tipo 'unico'): monto exacto. Sin `tipo` = rango (legacy).
    if (salarioRango.tipo === 'unico') {
      return salarioRango.monto ? `${currency} ${Number(salarioRango.monto).toLocaleString()}`.trim() : null;
    }
    if (!salarioRango.min && !salarioRango.max) return null;
    const min = salarioRango.min ? `${currency} ${Number(salarioRango.min).toLocaleString()}` : null;
    const max = salarioRango.max ? `${currency} ${Number(salarioRango.max).toLocaleString()}` : null;
    if (min && max) return `${min} — ${max}`;
    if (min) return `Desde ${min}`;
    if (max) return `Hasta ${max}`;
    return null;
  };

  const formatUbicacion = (ubicacion) => {
    if (!ubicacion) return null;
    return [ubicacion.city, ubicacion.province, ubicacion.country].filter(Boolean).join(', ') || null;
  };

  // ── PHASE HANDLERS ────────────────────────────────────────────────────────

  const handlePhaseChange = (phaseId) => {
    const phases = getPhases();
    const fromPhase = getCurrentPhase();
    const toPhase = phases.find((p) => p.id === phaseId);
    setPendingPhaseMove({ phaseId, fromPhase, toPhase });
    setPhaseDialogOpen(true);
  };

  const handleAdvancePhase = () => {
    const phases = getPhases();
    const currentPhase = getCurrentPhase();
    if (!currentPhase) return;
    const nextPhase = phases.find((p) => p.order === currentPhase.order + 1);
    if (!nextPhase) return;
    handlePhaseChange(nextPhase.id);
  };

  const handleRetreatPhase = () => {
    const phases = getPhases();
    const currentPhase = getCurrentPhase();
    if (!currentPhase) return;
    const prevPhase = phases.find((p) => p.order === currentPhase.order - 1);
    if (!prevPhase) return;
    handlePhaseChange(prevPhase.id);
  };

  const handleConfirmPhaseMove = async (note) => {
    if (!pendingPhaseMove) return;
    const { phaseId } = pendingPhaseMove;
    setPhaseDialogOpen(false);
    setPendingPhaseMove(null);
    setUpdatingPhase(true);
    try {
      await api.patch(
        `/positions/${positionId}/current-phase`,
        { phaseId, note: note || null },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setPosition((prev) => ({
        ...prev,
        pipeline: { ...prev.pipeline, currentPhaseId: phaseId },
      }));
      showAlert(t('ats.detail.phaseUpdated'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.phaseUpdateError'), 'error');
    } finally {
      setUpdatingPhase(false);
    }
  };

  // ── STAGE HANDLERS ────────────────────────────────────────────────────────

  const handleStageChange = (application, newStageId) => {
    if (newStageId === application.currentStageId) return;
    const allStages = getAllStages();
    const fromStage = allStages.find((s) => s.id === application.currentStageId);
    const toStage = allStages.find((s) => s.id === newStageId);
    setPendingStageMove({ application, newStageId, fromStage, toStage });
    setMoveDialogOpen(true);
  };

  const handleConfirmStageMove = async (note) => {
    if (!pendingStageMove) return;
    const { application, newStageId, fromStage, toStage } = pendingStageMove;
    const appId = application.uid || application.id;
    setMoveDialogOpen(false);
    setPendingStageMove(null);
    setUpdatingStage(appId);
    try {
      const res = await api.patch(
        `/applications/${appId}/stage`,
        {
          stageId: newStageId,
          previousStageId: application.currentStageId || null,
          positionId,
          note: note || null,
          fromStageName: fromStage?.name || null,
          toStageName: toStage?.name || null,
          fromPhaseName: fromStage?.phaseName || null,
          toPhaseName: toStage?.phaseName || null,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      // Historial actualizado que devuelve el backend: la nota del movimiento
      // queda visible en la ficha (y en Estadísticas) sin recargar. Si la
      // etapa consulta antecedentes sola, trae además la revisión nueva
      // (2026-10-13); se conserva la decisión del consultor.
      const { stageHistory, backgroundReview, backgroundCheckSkipped } = res?.data?.response?.data || {};
      setApplications((prev) =>
        prev.map((a) => ((a.uid || a.id) === appId
          ? {
            ...a,
            currentStageId: newStageId,
            ...(Array.isArray(stageHistory) ? { stageHistory } : {}),
            ...(backgroundReview ? { backgroundReview: { ...(a.backgroundReview || {}), ...backgroundReview } } : {}),
          }
          : a))
      );
      // Etapa con consulta automática y sin puntos (2026-11-01): se avisa
      const skippedMsg = skippedAfterMoveMessage(t, [backgroundCheckSkipped]);
      showAlert(skippedMsg || t('ats.detail.stageUpdated'), skippedMsg ? 'warning' : 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.stageUpdateError'), 'error');
    } finally {
      setUpdatingStage(null);
    }
  };

  // ── DISCARD / DELETE HANDLERS ─────────────────────────────────────────────

  const handleDiscard = async (application, emailOptions = {}) => {
    try {
      await api.patch(
        `/applications/${application.uid || application.id}/discard`,
        {
          sendEmail: !!emailOptions.sendEmail,
          emailSubject: emailOptions.emailSubject || '',
          emailBody: emailOptions.emailBody || '',
          senderName: emailOptions.senderName || '',
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setApplications((prev) =>
        prev.map((a) =>
          (a.uid || a.id) === (application.uid || application.id)
            ? { ...a, status: 'discarded' }
            : a
        )
      );
      showAlert(t('ats.detail.discarded'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.discardError'), 'error');
    }
  };

  const handleDelete = async (application) => {
    try {
      await api.delete(
        `/applications/${application.uid || application.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setApplications((prev) =>
        prev.filter((a) => (a.uid || a.id) !== (application.uid || application.id))
      );
      showAlert(t('ats.detail.applicationDeleted'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.applicationDeleteError'), 'error');
    }
  };

  // ── FINALIZE HANDLER ──────────────────────────────────────────────────────

  const handleFinalizeProcess = async (note = null) => {
    // El botón es un toggle: desde cualquier estado terminal (Cerrado o
    // Cancelado) reabre a Activo; desde el resto finaliza a Cerrado.
    // Suspender/Cancelar se hace desde el wizard de edición.
    const isTerminal = isTerminalStatus(position?.status);
    const newStatus = isTerminal ? 'active' : 'closed';
    setClosingProcess(true);
    try {
      await api.put(
        `/positions/${positionId}`,
        { status: newStatus, statusNote: note },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setPosition((prev) => ({ ...prev, status: newStatus }));
      showAlert(
        newStatus === 'closed'
          ? t('ats.detail.processClosed')
          : t('ats.detail.processReopened'),
        'success'
      );
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.processStatusError'), 'error');
    } finally {
      setClosingProcess(false);
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  if (loadingPosition) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  }

  const phases = getPhases();
  const currentPhase = getCurrentPhase();
  const allStages = getAllStages();
  const activeApplications = applications.filter((a) => a.status !== 'discarded');

  // Permiso de edición del proceso (solo ATS). Controla header, fases, kanban y candidatos.
  const canEdit = canEditPosition(position, user);

  return (
    <Box sx={{ width: '100%' }}>

      <AtsPositionHeader
        position={position}
        positionId={positionId}
        canEdit={canEdit}
        onBack={() => navigate('/ats/positions')}
        onEdit={() => navigate(`/ats/positions/${positionId}/edit`)}
        // Abre el detalle público de la vacante ({slug}.amandahr.ai/vacante/:id)
        onOpenPortal={() => window.open(getPositionUrl(positionId, publicSlug), '_blank')}
        onOpenSimulator={() => navigate(`/ats/positions/${positionId}/simulator`)}
        onDuplicate={() => navigate(`/ats/positions/${positionId}/duplicate`)}
        onDeleted={() => navigate('/ats/positions')}
        formatUbicacion={formatUbicacion}
        formatSalario={formatSalario}
      />

      <AtsPhaseBar
        phases={phases}
        currentPhase={currentPhase}
        position={position}
        canEdit={canEdit}
        updatingPhase={updatingPhase}
        closingProcess={closingProcess}
        onAdvancePhase={handleAdvancePhase}
        onRetreatPhase={handleRetreatPhase}
        onFinalizeProcess={handleFinalizeProcess}
        phaseHistory={position?.phaseHistory}
      />

      <Paper elevation={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab
              icon={<PeopleIcon />}
              iconPosition="start"
              label={`${t('ats.detail.candidates')} (${activeApplications.length})`}
            />
            <Tab icon={<InfoIcon />} iconPosition="start" label={t('ats.detail.info')} />
            <Tab icon={<PipelineIcon />} iconPosition="start" label={t('ats.detail.pipeline')} />
            <Tab icon={<StatsIcon />} iconPosition="start" label={t('ats.stats.tab')} />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>

          <TabPanel value={tabValue} index={0}>
            {canEdit && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                {/* Importar desde un archivo (PLAN_IMPORTACION_COMPUTRABAJO, fase 1) */}
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setImportOpen(true)}
                  sx={{ borderColor: '#6A5AF9', color: '#6A5AF9', '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' } }}
                >
                  {t('ats.import.button')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setAddApplicantsOpen(true)}
                  sx={{ borderColor: '#6A5AF9', color: '#6A5AF9', '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' } }}
                >
                  {t('ats.detail.addExistingApplicant')}
                </Button>
              </Box>
            )}
            <AtsApplicantsTab
              loading={loadingApplications}
              applications={applications}
              allStages={allStages}
              updatingStage={updatingStage}
              isSuperAdmin={isSuperAdmin}
              isClosed={isTerminalStatus(position?.status) || !canEdit}
              onStageChange={handleStageChange}
              onDiscard={handleDiscard}
              onDelete={handleDelete}
              rejectionTemplate={rejectionTemplate}
              autoSendDefault={!!position?.autoSendRejectionEmail}
              positionName={position?.name}
              orgName={organizationName}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <AtsPositionInfoTab position={position} formatSalario={formatSalario} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <AtsKanbanPipeline
              position={position}
              applications={applications}
              onApplicationsChange={setApplications}
              positionId={positionId}
              isClosed={isTerminalStatus(position?.status)}
              canEdit={canEdit}
              rejectionTemplate={rejectionTemplate}
              autoSendDefault={!!position?.autoSendRejectionEmail}
              positionName={position?.name}
              orgName={organizationName}
              cvScores={cvScores}
              onCvScoresRefresh={loadCvScores}
              onPositionChange={(patch) => setPosition((prev) => ({ ...prev, ...patch }))}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <AtsPositionStatsTab position={position} applications={applications} />
          </TabPanel>

        </Box>
      </Paper>

      {/* Dialog nota al cambiar etapa */}
      {pendingStageMove && (
        <AtsMoveStageDialog
          open={moveDialogOpen}
          onConfirm={handleConfirmStageMove}
          onCancel={() => { setMoveDialogOpen(false); setPendingStageMove(null); }}
          fromStage={pendingStageMove.fromStage}
          toStage={pendingStageMove.toStage}
          candidateName={
            pendingStageMove.application?.candidateName ||
            pendingStageMove.application?.candidateEmail
          }
        />
      )}

      {/* Dialog nota al cambiar fase */}
      {pendingPhaseMove && (
        <AtsMoveStageDialog
          open={phaseDialogOpen}
          onConfirm={handleConfirmPhaseMove}
          onCancel={() => { setPhaseDialogOpen(false); setPendingPhaseMove(null); }}
          fromStage={pendingPhaseMove.fromPhase}
          toStage={pendingPhaseMove.toPhase}
          isPhaseMove
        />
      )}

      {canEdit && (
        <AtsAddApplicantsDialog
          open={addApplicantsOpen}
          onClose={() => setAddApplicantsOpen(false)}
          positionId={positionId}
          projectId={position?.projectId}
          position={position}
          onAdded={reloadApplications}
        />
      )}

      {canEdit && (
        <AtsImportApplicantsDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          position={position}
          positionId={positionId}
          onImported={reloadApplications}
        />
      )}

    </Box>
  );
};

export default AtsPositionDetailLayout;